import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaPrint,
  FaChevronUp,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const BASE_URL = "https://nissi-india.com";

const ContractorPaymentReport = () => {
  // -------------------------------------------------
  // THEME
  // -------------------------------------------------
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // -------------------------------------------------
  // LOCAL STORAGE HELPER
  // -------------------------------------------------
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // -------------------------------------------------
  // LOGIN CONTEXT
  // -------------------------------------------------
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype") || "HO";
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  const branchid = Number(branchId || 0);
  const branchtype = branch || "HO";

  // -------------------------------------------------
  // COMMON HEADERS
  // -------------------------------------------------
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // -------------------------------------------------
  // UI STATE
  // -------------------------------------------------
  const [showFilters, setShowFilters] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  // -------------------------------------------------
  // FILTER STATE
  // -------------------------------------------------
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    contractorIdByName: "",
    contractorIdByCode: "",
    projectId: "",
    billNumber: "",
  });

  const [lastSearchParams, setLastSearchParams] = useState(null);

  // -------------------------------------------------
  // DROPDOWNS
  // -------------------------------------------------
  const [contractorNames, setContractorNames] = useState([]);
  const [contractorCodes, setContractorCodes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [bills, setBills] = useState([]);

  // -------------------------------------------------
  // TABLE + PAGINATION
  // -------------------------------------------------
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  // -------------------------------------------------
  // HELPERS
  // -------------------------------------------------
  const onChangeFilter = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const formatAmount = (val) => {
    const n = Number(val);
    return Number.isFinite(n) ? n.toFixed(2) : "0.00";
  };

  const formatDate = (val) => {
    if (!val) return "";
    const s = String(val);
    if (s.includes("T")) return s.slice(0, 10);
    return s;
  };

  const getSelectedContractorId = () => {
    return (
      Number(filters.contractorIdByCode || 0) ||
      Number(filters.contractorIdByName || 0) ||
      0
    );
  };

  const buildQuery = (obj) => {
    const sp = new URLSearchParams();
    Object.entries(obj).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        sp.set(k, String(v));
      }
    });
    return sp.toString();
  };

  // ✅ Print single row
  const handlePrint = (row) => {
    if (!row?.payId) {
      toast.error("payId missing for this row (backend must return fld_contractorpayid)");
      return;
    }

    window.open(
      `/PaymentVouchers/ContractorPaymentReport_Print?payid=${row.payId}`,
      "_blank"
    );
  };

  // ✅ Print All
  const handleAllPrint = () => {
    if (!lastSearchParams) {
      toast.error("Please Search first, then Print All.");
      return;
    }
    const qs = buildQuery(lastSearchParams);
    window.open(`/PaymentVouchers/ContractorPaymentReport_AllPrint?${qs}`, "_blank");
  };

  // -------------------------------------------------
  // FETCH DDLS
  // -------------------------------------------------
  const loadDDLs = async () => {
    if (!branchid) {
      toast.error("branchId missing in localStorage");
      return;
    }

    try {
      const [nameRes, codeRes, projRes, billRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/contractorReport/contractorNames`, {
          params: { branchid },
          headers: commonHeaders,
        }),
        axios.get(`${BASE_URL}/api/contractorReport/contractorCodes`, {
          params: { branchid },
          headers: commonHeaders,
        }),
        axios.get(`${BASE_URL}/api/contractorReport/projects`, {
          params: { branchid, branchtype },
          headers: commonHeaders,
        }),
        axios.get(`${BASE_URL}/api/contractorReport/bills`, {
          params: { branchid },
          headers: commonHeaders,
        }),
      ]);

      if (nameRes.data.success) setContractorNames(nameRes.data.list || []);
      else toast.error(nameRes.data.message || "Failed to load contractor names");

      if (codeRes.data.success) setContractorCodes(codeRes.data.list || []);
      else toast.error(codeRes.data.message || "Failed to load contractor codes");

      if (projRes.data.success) setProjects(projRes.data.list || []);
      else toast.error(projRes.data.message || "Failed to load projects");

      if (billRes.data.success) setBills(billRes.data.list || []);
      else toast.error(billRes.data.message || "Failed to load bills");
    } catch (err) {
      console.error(err);
      toast.error("Server error while loading dropdowns");
    }
  };

  // -------------------------------------------------
  // SEARCH API (Pagination)
  // -------------------------------------------------
  const fetchReport = async (pageNo = 1) => {
    if (!branchid) {
      toast.error("branchId missing in localStorage");
      return;
    }

    if (!filters.fromDate || !filters.toDate) {
      toast.error("Please select From Date and To Date");
      return;
    }

    const contractorid = getSelectedContractorId();
    const projectid = Number(filters.projectId || 0);
    const billno = filters.billNumber || "";

    const params = {
      branchid,
      contractorid,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      projectid,
      billno,
      page: pageNo,
      pageSize,
    };

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/contractorReport/search`, {
        params,
        headers: commonHeaders,
      });

      if (!res.data.success) {
        toast.error(res.data.message || "Failed to load report");
        return;
      }

      const list = res.data.list || [];

      setRows(
        list.map((r, idx) => ({
          payId: r.fld_contractorpayid ?? r.payId ?? r.id,
          slNo: (pageNo - 1) * pageSize + idx + 1,
          billNumber: r.fld_billno || r.billno || "-",
          date: r.fld_date || r.date || "",
          contractorName: r.fld_contractorname || r.contractorname || "-",
          projectName: r.fld_projectname || r.projectname || "-",
          amount: r.fld_paidamount ?? r.amount ?? 0,
          pendingAmount: r.fld_balamount ?? r.pendingamount ?? 0,
          userName: r.fld_username || r.username || "-",
        }))
      );

      setTotal(res.data.total || 0);
      setPage(res.data.page || pageNo);
      setHasSearched(true);

      setLastSearchParams({
        branchid,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        contractorid,
        projectid,
        billno,
      });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Server error while loading data");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    toast.info("Searching...");
    fetchReport(1);
  };

  const goPrev = () => {
    if (page > 1) fetchReport(page - 1);
  };

  const goNext = () => {
    if (page < totalPages) fetchReport(page + 1);
  };

  // -------------------------------------------------
  // INIT
  // -------------------------------------------------
  useEffect(() => {
    loadDDLs();
  }, [branchid, branchtype, commonHeaders]);

  // -------------------------------------------------
  // UI
  // -------------------------------------------------
  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">CONTRACTOR PAYMENT REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Contractor Payment Report
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                Branch: {branchid || "-"} ({branchtype})
              </span>
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {showFilters && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) => onChangeFilter("fromDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) => onChangeFilter("toDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div />

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Contractor Name
                  </label>
                  <select
                    value={filters.contractorIdByName}
                    onChange={(e) => {
                      onChangeFilter("contractorIdByName", e.target.value);
                      onChangeFilter("contractorIdByCode", "");
                    }}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {contractorNames.map((c) => (
                      <option key={c.contractorid} value={c.contractorid}>
                        {c.contractorname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Contractor Code / Id
                  </label>
                  <select
                    value={filters.contractorIdByCode}
                    onChange={(e) => {
                      onChangeFilter("contractorIdByCode", e.target.value);
                      onChangeFilter("contractorIdByName", "");
                    }}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {contractorCodes.map((c) => (
                      <option key={c.contractorid} value={c.contractorid}>
                        {c.contractorcode}
                      </option>
                    ))}
                  </select>
                </div>

                <div />

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Project Name
                  </label>
                  <select
                    value={filters.projectId}
                    onChange={(e) => onChangeFilter("projectId", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {projects.map((p) => (
                      <option key={p.projectid} value={p.projectid}>
                        {p.projectname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Bill Number
                  </label>
                  <select
                    value={filters.billNumber}
                    onChange={(e) => onChangeFilter("billNumber", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {bills.map((b, idx) => (
                      <option key={idx} value={b.billno}>
                        {b.billno}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-center mb-6 gap-6">
                <button
                  onClick={handleSearch}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaSearch />
                  Search
                </button>

                <button
                  onClick={handleAllPrint}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaPrint />
                  Print All
                </button>
              </div>
            </div>
          )}

          {hasSearched && (
            <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Sl.No</th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Bill Number</th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Date</th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Contractor Name</th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Project Name</th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Amount</th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Pending Amount</th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">User Name</th>
                      <th className="px-3 py-3 text-center font-semibold">Print</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {loading ? (
                      <tr>
                        <td colSpan={9} className="text-center py-6">Loading...</td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-6">No data found</td>
                      </tr>
                    ) : (
                      rows.map((r, idx) => (
                        <tr
                          key={idx}
                          className={`transition-colors duration-200 ${
                            isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"
                          }`}
                        >
                          <td className={`px-3 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {r.slNo}
                          </td>

                          <td className={`px-3 py-3 text-center border-r ${isDark ? "border-gray-700 text-blue-400" : "border-gray-200 text-blue-600"}`}>
                            {r.billNumber}
                          </td>

                          <td className={`px-3 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {formatDate(r.date)}
                          </td>

                          <td className={`px-3 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {r.contractorName}
                          </td>

                          <td className={`px-3 py-3 text-left border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {r.projectName}
                          </td>

                          <td className={`px-3 py-3 text-right border-r font-mono ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {formatAmount(r.amount)}
                          </td>

                          <td className={`px-3 py-3 text-right border-r font-mono ${isDark ? "border-gray-700 text-orange-400" : "border-gray-200 text-orange-600"}`}>
                            {formatAmount(r.pendingAmount)}
                          </td>

                          <td className={`px-3 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {r.userName}
                          </td>

                          <td className={`px-3 py-3 text-center ${isDark ? "text-white" : "text-gray-900"}`}>
                            <button
                              onClick={() => handlePrint(r)}
                              className={`p-2 rounded-lg shadow-sm hover:shadow-md transform hover:scale-105 transition-all duration-200 ${
                                isDark
                                  ? "bg-gray-700 hover:bg-gray-600 text-blue-400"
                                  : "bg-gray-100 hover:bg-gray-200 text-blue-600"
                              }`}
                              title="Print this record"
                            >
                              <FaPrint />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {hasSearched && (
            <div className="flex justify-between items-center mt-4">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Total: <b>{total}</b> | Page: <b>{page}</b> / {totalPages}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={goPrev}
                  disabled={page <= 1 || loading}
                  className={`px-3 py-2 rounded-lg font-semibold shadow ${
                    page <= 1 || loading
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : `${buttonClass} ${hoverClass} text-white`
                  }`}
                >
                  <FaChevronLeft />
                </button>

                <button
                  onClick={goNext}
                  disabled={page >= totalPages || loading}
                  className={`px-3 py-2 rounded-lg font-semibold shadow ${
                    page >= totalPages || loading
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : `${buttonClass} ${hoverClass} text-white`
                  }`}
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default ContractorPaymentReport;